import React from 'react';
import { PEIRecordsInfo } from '../PEIRecordsInfo';
import styles from '@/styles/PEIScalableTable.module.scss';
import { PEIImage } from '../PEIImage';
import { KeyContactIcon } from '../KeyContactIcon';
import { useWindowSize, FlexibleTableProps, PEILink } from '@/common';
import { generatePathsOfAssets } from '@/utils';
import { useAppContext } from '@/components';
import { ZephrFeature } from '@/components/ZephrFeature';

const breakPoint = 1024;

const PLACEHOLDER_CELLS = ['Sample Record Name', 'Example value', 'City, Country', 'sample@example.com'];
const PLACEHOLDER_ROWS = 4;

const TablePlaceholder = ({ headers, centerCells }: { headers: Array<string>; centerCells?: boolean }) => (
  <section className={styles['table-container']}>
    <table className={`${styles.table} ${centerCells ? styles['table-centered'] : ''}`} role='presentation'>
      <tbody>
        {React.Children.toArray(
          Array.from({ length: PLACEHOLDER_ROWS }).map(() => (
            <tr className={styles.row}>
              {React.Children.toArray(
                headers.map((_header: string, index: number) => (
                  <td className={styles.cell}>
                    <div className={styles['data-container']}>
                      {PLACEHOLDER_CELLS[index % PLACEHOLDER_CELLS.length]}
                    </div>
                  </td>
                ))
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </section>
);

export const PEIScalableTable = ({
  title,
  count,
  headers,
  data,
  start,
  end,
  institutionName,
  componentName,
  centerCells,
}: {
  title: string;
  count?: number;
  headers: Array<string>;
  data: FlexibleTableProps;
  institutionName: string;
  start: number;
  end: number;
  componentName?: string;
  centerCells?: boolean;
}) => {
  const {
    state: { isECJest },
  } = useAppContext();
  const { width } = useWindowSize();
  return (
    <section className={styles.container}>
      <h3 className={styles.heading} data-testid='table-title-id'>
        {title}
        {count !== undefined && (
          <>
            {': '}
            <ZephrFeature inline placeholder={<>00</>}>
              {count}
            </ZephrFeature>
          </>
        )}
      </h3>
      <ZephrFeature
        placeholder={<TablePlaceholder headers={headers} centerCells={centerCells} />}
      >
        <section className={styles['table-container']}>
          <table
          className={`${styles.table} ${centerCells ? styles['table-centered'] : ''}`}
          role='table'
        >
          <thead className={styles['row-header']}>
            <tr className={styles.row}>
              {React.Children.toArray(
                headers.map((title: string) => (
                  <th className={`${styles['cell-header']} ${styles['cell']} `}>{title}</th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {React.Children.toArray(
              data.map(
                (row: Array<{ text: string }>): JSX.Element => (
                  <tr className={styles.row}>
                    {React.Children.toArray(
                      row.map(
                        (
                          {
                            text,
                            icon,
                            link,
                            keyContact,
                          }: {
                            text: string;
                            icon?: string;
                            link?: string;
                            keyContact?: boolean;
                          },
                          index
                        ) => (
                          <td className={styles.cell}>
                            {keyContact && (
                              <span className={styles['key-contact']}>
                                <KeyContactIcon className={styles['key-contact-icon']} />
                                Key Contact
                              </span>
                            )}
                            <div className={styles['data-container']}>
                              {!isECJest && index != 0 && (
                                <span className={styles['headers-in-mobile']}>
                                  {headers[index]}
                                </span>
                              )}
                              {icon && (
                                <span className={styles['icon-container']}>
                                  <PEIImage
                                    src={generatePathsOfAssets(icon)}
                                    alt={`${icon} image`}
                                    width={21.12}
                                    height={20}
                                    className={styles.icon}
                                  />
                                </span>
                              )}
                              <span className={icon === 'lock' ? styles.hide : ''}>
                                {link && icon !== 'lock' ? (
                                  <PEILink className={styles['text-link']} href={link}>
                                    {text}
                                  </PEILink>
                                ) : (
                                  text
                                )}

                                {index === 0 && icon === 'lock' && width <= breakPoint && (
                                  <span className={styles['extra-text']}> {headers[index]}</span>
                                )}
                              </span>
                            </div>
                          </td>
                        )
                      )
                    )}
                  </tr>
                )
              )
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={headers.length} className={styles['table-footer']}>
                <PEIRecordsInfo
                  start={start}
                  end={end}
                  institutionName={institutionName}
                  componentName={componentName}
                />
              </td>
            </tr>
          </tfoot>
          </table>
        </section>
      </ZephrFeature>
    </section>
  );
};
